import React from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { useAuthForm } from "@/contexts/AuthFormProvider";

const CallToActionSection: React.FC = () => {
  const { showRegisterForm } = useAuthForm();
  return (
    <div className="relative py-12 md:py-16 overflow-hidden">
      {/* Animated background particles */}
      {[...Array(12)].map((_, i) => (
        <motion.div
          key={`cta-particle-${i}`}
          className="absolute rounded-full bg-gradient-to-r from-blue-400/15 to-purple-400/15"
          style={{
            width: Math.random() * 6 + 3,
            height: Math.random() * 6 + 3,
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
          }}
          animate={{
            y: [0, -30, 0],
            opacity: [0, 0.6, 0],
            scale: [0.5, 1.2, 0.5],
          }}
          transition={{
            duration: Math.random() * 3 + 2,
            repeat: Infinity,
            delay: Math.random() * 2,
            ease: "easeInOut"
          }}
        />
      ))}

      <div className="relative z-10 max-w-3xl mx-auto px-8 text-center">
        <motion.div
          initial={{ opacity: 0, scale: 0.9 }}
          whileInView={{ opacity: 1, scale: 1 }}
          transition={{ duration: 0.8 }}
          viewport={{ once: true, margin: "-10%" }}
        >
          {/* Pulsing accent line */}
          <motion.div 
            className="w-24 h-1 bg-gradient-to-r from-blue-400 to-purple-400 rounded-full mx-auto mb-6"
            animate={{ 
              opacity: [0.5, 1, 0.5],
              scaleX: [1, 1.2, 1]
            }}
            transition={{ duration: 2, repeat: Infinity }}
          />

          <motion.h2 
            className="text-3xl lg:text-4xl font-bold text-white leading-tight mb-4"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            viewport={{ once: true }}
          >
            Ready to 
            <span className="bg-gradient-to-r from-blue-400 via-purple-400 to-cyan-400 bg-clip-text text-transparent">
              {" "}Transform{" "}
            </span>
            Your Workflow?
          </motion.h2>

          <motion.p 
            className="text-lg text-gray-300 leading-relaxed mb-8 max-w-2xl mx-auto"
            initial={{ opacity: 0, y: 15 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.3 }}
            viewport={{ once: true }}
          >
            Start building enterprise backends in minutes with ShredsAI
          </motion.p>

          <motion.div
            className="flex justify-center"
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.4 }}
            viewport={{ once: true }}
          >
            <motion.button
              onClick={() => {
                window.lintrk('track', { conversion_id: 23344122 });
                showRegisterForm();
              }}
              className="group relative px-10 py-5 rounded-xl bg-gradient-to-r from-blue-600 via-purple-600 to-cyan-600 text-white font-bold text-lg shadow-xl overflow-hidden"
              whileHover={{ 
                scale: 1.05,
                y: -2,
                boxShadow: "0 15px 35px rgba(59, 130, 246, 0.4)"
              }}
              whileTap={{ scale: 0.98 }}
              animate={{
                boxShadow: [
                  "0 10px 25px rgba(59, 130, 246, 0.2)",
                  "0 15px 35px rgba(147, 51, 234, 0.3)",
                  "0 10px 25px rgba(59, 130, 246, 0.2)"
                ]
              }}
              transition={{ 
                boxShadow: { duration: 3, repeat: Infinity },
                scale: { duration: 0.2 }
              }}
            >
              {/* Animated shine effect */}
              <motion.div
                className="absolute inset-0 bg-gradient-to-r from-white/0 via-white/20 to-white/0"
                animate={{ x: ['-100%', '200%'] }}
                transition={{ duration: 2.5, repeat: Infinity, repeatDelay: 3 }}
              />
              
              <div className="relative flex items-center space-x-3">
                <span>Start Building Now</span>
                <motion.div
                  whileHover={{ x: 3 }}
                  transition={{ duration: 0.2 }}
                >
                  <ArrowRight className="w-5 h-5" />
                </motion.div>
              </div>
            </motion.button>
          </motion.div>
        </motion.div>
      </div>
    </div>
  );
};

export default CallToActionSection;